/*
 * idus — iCell: keeps the two PDP buttons ("הוסף לסל" / "שריון באילת") in sync with the
 * selected variant.
 *
 * Patches SHTEilatPriceUpdater (assets/theme.js). Two scripts write to the same buttons after
 * every variant change, each after its own network request:
 *   1. the theme's toggleAddButton (component-add-to-cart.js) — by variant.available;
 *   2. SHTEilatPriceUpdater — by data-pickup-available / data-eilat-available from the server.
 * Whichever finished last won, and stale responses from a previous variant were never
 * discarded, so the same variant could end up with different button states.
 *
 * This patch:
 *   - aborts the previous request and ignores any response that is not the latest;
 *   - keeps both buttons disabled while a request is pending;
 *   - makes data-pickup-available / data-eilat-available the final word: a MutationObserver
 *     re-applies them whenever another script toggles `disabled` (buttons in a "loading"
 *     add-to-cart state are left alone);
 *   - applies the state on page load too.
 *
 * Loaded (defer) from snippets/product-atc-buttons.liquid. theme.js is deferred too but sits at
 * the end of <body>, so it may run after this file: the patch is applied on DOMContentLoaded,
 * which fires after every deferred script and before theme.js's own DOMContentLoaded listener
 * creates the SHTEilatPriceUpdater instance (ours is registered first).
 */
(function () {
  function patch() {
    if (window.__icellAtcSyncPatched) return;
    if (typeof SHTEilatPriceUpdater === "undefined") return;
    window.__icellAtcSyncPatched = true;

    var P = SHTEilatPriceUpdater.prototype;
    var origBindEvents = P.bindEvents;

    P._containers = function () {
      return [this.atcButtonsContainer, this.stickyAtcButtonsContainer].filter(
        Boolean,
      );
    };

    P._setDisabled = function (btn, disabled) {
      if (!btn) return;
      if (!disabled && btn.classList.contains("loading")) return; // add-to-cart in progress
      if (btn.hasAttribute("disabled") === disabled) return; // no-op -> no mutation -> no loop
      if (disabled) btn.setAttribute("disabled", "disabled");
      else btn.removeAttribute("disabled");
    };

    P.applyLocationDisabledState = function () {
      var self = this;
      this._containers().forEach(function (c) {
        var pending = c.dataset.pending === "true";
        self._setDisabled(
          c.querySelector(".js-product-form-submit-btn"),
          pending || c.dataset.pickupAvailable !== "true",
        );
        self._setDisabled(
          c.querySelector(".js-product-form-submit-btn-eilat"),
          pending || c.dataset.eilatAvailable !== "true",
        );
      });
    };

    P.bindEvents = function () {
      origBindEvents.call(this);
      var self = this;
      this.applyLocationDisabledState();
      var observer = new MutationObserver(function () {
        self.applyLocationDisabledState();
      });
      this._containers().forEach(function (c) {
        observer.observe(c, {
          attributes: true,
          attributeFilter: ["disabled", "class"],
          subtree: true,
          childList: true,
        });
      });
    };

    P.applyUnavailableState = function () {
      if (this._abortCtrl) this._abortCtrl.abort();
      this._abortCtrl = null;
      this._containers().forEach(function (c) {
        delete c.dataset.pending;
        c.dataset.pickupAvailable = "false";
        c.dataset.eilatAvailable = "false";
      });
      this.applyLocationDisabledState();
    };

    P.updateVariantUI = function (currentVariant) {
      var sid = this.sectionId;
      var variantInput =
        document.querySelector("#product-form-" + sid + ' input[name="id"]') ||
        document.querySelector('form[id*="' + sid + '"] input[name="id"]') ||
        document.querySelector('.js-main-product-form input[name="id"]') ||
        document.querySelector(
          'form[data-type="add-to-cart-form"] input[name="id"]',
        );
      var variantId =
        (currentVariant && currentVariant.id) ||
        (variantInput && variantInput.value);
      if (!variantId) return;

      var self = this;
      if (this._abortCtrl) this._abortCtrl.abort();
      var ctrl = (this._abortCtrl = new AbortController());

      this._containers().forEach(function (c) {
        c.dataset.pending = "true";
      });
      this.applyLocationDisabledState();

      var copy = function (doc, target, id, withAvailability) {
        if (!target) return;
        var fresh = doc.querySelector("#" + id);
        if (!fresh) return;
        if (withAvailability) {
          target.dataset.pickupAvailable =
            fresh.dataset.pickupAvailable || "false";
          target.dataset.eilatAvailable =
            fresh.dataset.eilatAvailable || "false";
        }
        target.innerHTML = fresh.innerHTML;
      };

      fetch(this.productUrl + "?variant=" + variantId + "&section_id=" + sid, {
        method: "GET",
        headers: { Accept: "text/html", "Cache-Control": "no-cache" },
        signal: ctrl.signal,
      })
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          if (ctrl !== self._abortCtrl) return; // a newer variant was selected meanwhile
          var doc = new DOMParser().parseFromString(html, "text/html");
          copy(
            doc,
            self.eilatPriceContainer,
            "productEilatPrice-" + sid,
            false,
          );
          copy(doc, self.atcButtonsContainer, "atcButtons-" + sid, true);
          copy(
            doc,
            self.stickyAtcButtonsContainer,
            "stickyAtcButtons-" + sid,
            true,
          );
          copy(doc, self.floatCardContainer, "productFloatCard-" + sid, false);
        })
        .catch(function (err) {
          if (err && err.name === "AbortError") return;
          console.error("Product variant UI update failed:", err);
          if (ctrl !== self._abortCtrl) return;
          // Unknown state -> fail closed rather than leave the previous variant's buttons enabled.
          self._containers().forEach(function (c) {
            c.dataset.pickupAvailable = "false";
            c.dataset.eilatAvailable = "false";
          });
        })
        .finally(function () {
          if (ctrl !== self._abortCtrl) return;
          self._abortCtrl = null;
          self._containers().forEach(function (c) {
            delete c.dataset.pending;
          });
          self.applyLocationDisabledState();
        });
    };
  }

  if (typeof SHTEilatPriceUpdater !== "undefined") patch();
  else document.addEventListener("DOMContentLoaded", patch);
})();
